import { useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useCollections } from '@/features/collections/useCollections';
import { useGenerationOpen } from '@/features/collections/useGenerationOpen';
import { useCreditsQuery } from '@/features/credits/useCreditsQuery';
import { useCreateGenerationMutation } from '@/features/generation/useCreateGenerationMutation';
import { CreateScreen, type CreateRequest } from '@/features/generation/wizard/CreateScreen';
import type { SelectionPreset } from '@/features/generation/wizard/wizard-state';
import { RewardedAdButton } from '@/features/rewards/RewardedAdButton';
import { track } from '@/services/analytics';

/**
 * Мастер создания портрета для одной коллекции.
 *
 * Коллекция берётся из общего каталога по slug'у из адреса: ссылка на мастер
 * остаётся человекочитаемой и переживает перезагрузку страницы. Ядро
 * по-прежнему не знает названий коллекций — незнакомый slug просто не
 * находится в каталоге и честно об этом сообщает.
 */
export function CreatePage() {
  const { collectionSlug } = useParams<{ collectionSlug: string }>();
  const navigate = useNavigate();
  // «Повторить» переносит выбор состоянием перехода, а не адресом: это
  // подсказка для одного открытия мастера, а не часть ссылки, которой делятся.
  const { state } = useLocation() as { state: { preset?: SelectionPreset } | null };
  const { data, isPending, isError, refetch } = useCollections();
  const generationOpen = useGenerationOpen();
  const credits = useCreditsQuery();
  const mutation = useCreateGenerationMutation();

  // Экран коллекции открыт — первая точка воронки, за которой идёт выбор
  // параметров и загрузка фото.
  useEffect(() => {
    if (collectionSlug) {
      track('collection_viewed', { collection: collectionSlug });
    }
  }, [collectionSlug]);

  if (isPending) {
    return <p className="px-6 py-8 text-text-2">Загрузка коллекции…</p>;
  }

  if (isError) {
    return (
      <div className="px-6 py-8">
        <p className="text-danger">Не удалось загрузить коллекцию. Попробуйте ещё раз.</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-2 text-sm text-accent hover:underline"
        >
          Повторить
        </button>
      </div>
    );
  }

  const collection = (data ?? []).find((candidate) => candidate.slug === collectionSlug);

  if (!collection) {
    return <p className="px-6 py-8 text-text-2">Коллекция не найдена. Выберите её на главной.</p>;
  }

  const create = (request: CreateRequest) => {
    mutation.mutate(
      {
        collectionSlug: collection.slug,
        selections: request.selections,
        photos: request.photos,
        consent: request.consent,
      },
      {
        onSuccess: (result) => {
          void navigate(
            result.kind === 'photoshoot'
              ? `/photoshoot/${result.photoshootId}`
              : `/result/${result.generationId}`,
          );
        },
      },
    );
  };

  return (
    <main className="flex flex-col">
      <h1 className="px-4 pt-8 font-display text-h1 text-text">{collection.title}</h1>
      <p className="px-4 pt-1 text-sm text-text-2">{collection.description}</p>

      <CreateScreen
        collection={collection}
        status={mutation.status === 'pending' ? 'pending' : 'idle'}
        errorMessage={mutation.error?.message ?? null}
        preset={state?.preset}
        credits={credits.data}
        generationOpen={generationOpen}
        outOfCreditsAction={
          <div className="flex flex-col gap-3">
            <Link
              to="/credits"
              className="btn btn-primary px-4 py-3 text-sm"
            >
              Пополнить кредиты
            </Link>
            <RewardedAdButton />
          </div>
        }
        onCreate={create}
      />
    </main>
  );
}
